import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Home.css';
import { fetchAds } from '../services/adService';
import { Helmet } from 'react-helmet-async';
import { getAdLink } from '../utils/adUtils';
import Spinner from '../components/Spinner';

const Home = () => {
    const navigate = useNavigate();
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategory, setFilterCategory] = useState('buy');
    const [filterCondition, setFilterCondition] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterLocation, setFilterLocation] = useState('');
    const [isFilterExpanded, setIsFilterExpanded] = useState(false);

    useEffect(() => {
        const loadAds = async () => {
            try {
                const fetchedAds = await fetchAds();
                // Sort by date descending
                fetchedAds.sort((a, b) => {
                    const dateA = a.createdAt?.seconds || 0;
                    const dateB = b.createdAt?.seconds || 0;
                    return dateB - dateA;
                });
                setAds(fetchedAds);
            } catch (error) {
                console.error("Failed to load ads:", error);
            } finally {
                setLoading(false);
            }
        };
        loadAds();
    }, []);

    const buildQueryString = () => {
        const params = new URLSearchParams();
        if (searchQuery) params.set('search', searchQuery);
        if (filterCondition) params.set('condition', filterCondition);
        if (filterType) params.set('vehicleType', filterType);
        if (filterLocation) params.set('location', filterLocation);
        return params.toString();
    };

    const handleSearch = (e) => {
        e.preventDefault();
        const qs = buildQueryString();
        navigate(`/${filterCategory}${qs ? `?${qs}` : ''}`);
    };

    const handleApplyFilters = () => {
        setIsFilterExpanded(false);
        const qs = buildQueryString();
        navigate(`/${filterCategory}${qs ? `?${qs}` : ''}`);
    };

    const resetFilters = () => {
        setSearchQuery('');
        setFilterCategory('buy');
        setFilterCondition('');
        setFilterType('');
        setFilterLocation('');
    };

    const hasActiveFilters = searchQuery || filterCategory !== 'buy' || filterCondition || filterType || filterLocation;

    // Filtered ads
    const filteredAds = useMemo(() => {
        return ads.filter(ad => {
            const matchesSearch = !searchQuery ||
                ad.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                ad.vehicleMake?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                ad.model?.toLowerCase().includes(searchQuery.toLowerCase());

            const matchesCondition = !filterCondition || ad.vehicleCondition === filterCondition;
            const matchesType = !filterType || ad.vehicleType === filterType;
            const matchesLocation = !filterLocation ||
                ad.location?.toLowerCase().includes(filterLocation.toLowerCase());

            return matchesSearch && matchesCondition && matchesType && matchesLocation;
        });
    }, [ads, searchQuery, filterCondition, filterType, filterLocation]);

    const vehicleTypes = ["Car", "Van", "SUV", "Jeep", "Motorbike", "Three Wheeler", "Truck", "Bus", "Lorry", "Bicycle"];
    const conditions = ["New", "Used", "Reconditioned"];

    return (
        <div className="home-container">
            <Helmet>
                <title>RiyaMaga | Buy, Sell &amp; Rent Vehicles in Sri Lanka | riyamaga.site</title>
                <meta name="description" content="Find your dream car, van, SUV or bike on RiyaMaga — Sri Lanka's most trusted vehicle marketplace. Buy, sell and rent vehicles, parts and find service stations." />
                <meta name="keywords" content="RiyaMaga, vehicles Sri Lanka, buy cars Sri Lanka, sell cars Sri Lanka, rent vehicles Sri Lanka, used cars, motorbikes, vans, SUV, vehicle parts, service stations Sri Lanka, riyamaga.site" />
                <link rel="canonical" href="https://riyamaga.site/" />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content="RiyaMaga — Find Your Dream Ride in Sri Lanka" />
                <meta property="og:description" content="The largest marketplace to buy, sell, and rent vehicles in Sri Lanka. Cars, vans, bikes, parts and service stations." />
                <meta property="og:url" content="https://riyamaga.site/" />
                <meta property="og:image" content="https://riyamaga.site/logo192.png" />
                <meta property="og:site_name" content="RiyaMaga" />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="RiyaMaga | Sri Lanka Vehicle Marketplace" />
                <meta name="twitter:description" content="Buy, sell and rent vehicles in Sri Lanka. Cars, vans, bikes, parts and more." />
                <meta name="twitter:image" content="https://riyamaga.site/logo192.png" />

                {/* JSON-LD: WebSite + Organization */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@graph": [
                            {
                                "@type": "WebSite",
                                "@id": "https://riyamaga.site/#website",
                                "url": "https://riyamaga.site/",
                                "name": "RiyaMaga",
                                "description": "Sri Lanka's vehicle marketplace",
                                "potentialAction": {
                                    "@type": "SearchAction",
                                    "target": {
                                        "@type": "EntryPoint",
                                        "urlTemplate": "https://riyamaga.site/?q={search_term_string}"
                                    },
                                    "query-input": "required name=search_term_string"
                                }
                            },
                            {
                                "@type": "Organization",
                                "@id": "https://riyamaga.site/#organization",
                                "name": "RiyaMaga",
                                "url": "https://riyamaga.site/",
                                "logo": {
                                    "@type": "ImageObject",
                                    "url": "https://riyamaga.site/logo512.png"
                                },
                                "sameAs": [
                                    "https://riyamaga.site"
                                ]
                            }
                        ]
                    })}
                </script>
            </Helmet>


            {/* 1. Search & Filter Section (First) */}
            <section className="home-search-section">
                <h1 className="home-search-title">Find Your Dream Ride</h1>
                <p className="home-search-subtitle">Sri Lanka's most trusted vehicle marketplace</p>

                <form className="home-search-bar" onSubmit={handleSearch}>
                    <i className="fa-solid fa-magnifying-glass home-search-icon"></i>
                    <input
                        type="text"
                        placeholder="Search by brand, model, or title..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button type="submit" className="btn-search">Search</button>
                </form>

                {/* Desktop Quick Filters & Mobile Trigger */}
                <div className="home-quick-search-actions">
                    <button className="btn-mobile-filter-trigger" onClick={() => setIsFilterExpanded(true)}>
                        <i className="fa-solid fa-sliders"></i>
                        <span>Quick Filters</span>
                    </button>
                </div>

                {/* Quick Filters - Desktop Inline, Mobile Popup */}
                <div className={`home-quick-filters-container ${isFilterExpanded ? 'mobile-expanded' : ''}`}>
                    {isFilterExpanded && <div className="filter-overlay-bg" onClick={() => setIsFilterExpanded(false)}></div>}

                    <div className="home-quick-filters">
                        {isFilterExpanded && (
                            <div className="mobile-filter-header">
                                <h3>Quick Filters</h3>
                                <button className="btn-close-filter" onClick={() => setIsFilterExpanded(false)}>
                                    <i className="fa-solid fa-xmark"></i>
                                </button>
                            </div>
                        )}

                        <div className="home-filter-group">
                            <select
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                                className="home-filter-select"
                            >
                                <option value="buy">Buy Vehicle</option>
                                <option value="rent">Rent Vehicle</option>
                                <option value="parts">Parts / Accessories</option>
                                <option value="service-stations">Service Stations</option>
                            </select>
                        </div>
                        <div className="home-filter-group">
                            <select
                                value={filterCondition}
                                onChange={(e) => setFilterCondition(e.target.value)}
                                className="home-filter-select"
                            >
                                <option value="">All Conditions</option>
                                {conditions.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div className="home-filter-group">
                            <select
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                className="home-filter-select"
                            >
                                <option value="">All Types</option>
                                {vehicleTypes.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div className="home-filter-group">
                            <input
                                type="text"
                                placeholder="Location..."
                                value={filterLocation}
                                onChange={(e) => setFilterLocation(e.target.value)}
                                className="home-filter-input"
                            />
                        </div>
                        {hasActiveFilters && (
                            <button className="home-filter-reset" onClick={resetFilters}>
                                <i className="fa-solid fa-xmark"></i> Clear
                            </button>
                        )}

                        {isFilterExpanded && (
                            <button className="btn-apply-filters" onClick={handleApplyFilters}>
                                Show Results
                            </button>
                        )}
                    </div>
                </div>

            </section>

            {/* 2. Ads Section */}
            <section id="ads" className="home-section">

                {loading ? (
                    <Spinner message="Loading the latest vehicles..." />
                ) : (
                    <div className="ads-grid">
                        {filteredAds.length > 0 ? (
                            filteredAds.map((ad) => (
                                <Link to={getAdLink(ad)} key={ad.id} className="ad-card" style={{ textDecoration: 'none', color: 'inherit' }}>
                                    <div className="ad-body">
                                        <img src={ad.image || 'https://placehold.co/600x400/1e293b/ffffff?text=No+Image'} alt={ad.title} className="ad-image" />
                                        <div className="ad-info">
                                            <div className="ad-title-row">
                                                <h3 className="ad-title">
                                                    {ad.title || `${ad.vehicleMake || ''} ${ad.model || ''}`.trim() || 'Vehicle Ad'}
                                                    {ad.manufacturedYear && <span className="ad-title-year"> ({ad.manufacturedYear})</span>}
                                                </h3>
                                                {ad.vehicleCondition && (
                                                    <span className="ad-condition">{ad.vehicleCondition}</span>
                                                )}
                                            </div>
                                            <p className="ad-location">{ad.location}</p>
                                            <p className="ad-price">{ad.price}</p>
                                            <div className="ad-mileage-row">
                                                <p className="ad-mileage">{ad.mileage ? `${ad.mileage} (km)` : ''}</p>
                                                <p className="ad-date">
                                                    {ad.createdAt?.seconds ? new Date(ad.createdAt.seconds * 1000).toLocaleDateString() : 'Recently'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            ))
                        ) : (
                            <div style={{ textAlign: 'center', width: '100%', padding: '2rem', color: 'var(--color-text-secondary)' }}>
                                {hasActiveFilters ? (
                                    <>
                                        <p>No vehicles found matching your filters.</p>
                                        <button onClick={resetFilters} style={{ background: 'none', border: 'none', color: 'var(--color-accent)', textDecoration: 'underline', cursor: 'pointer', marginTop: '1rem' }}>
                                            Clear all filters
                                        </button>
                                    </>
                                ) : 'No ads found.'}
                            </div>
                        )}
                    </div>
                )}
                <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                    <Link to="/buy" className="btn-post-ad" style={{ background: 'transparent', border: '2px solid var(--color-accent)', color: 'var(--color-accent)' }}>
                        View All Ads
                    </Link>
                </div>
            </section>

            {/* 3. Categories Section */}
            <section className="home-section">
                <h2 className="section-title">Browse by Category</h2>
                <div className="category-grid-wrapper">
                    <div className="category-grid">
                        <Link to="/buy?type=Car" className="category-card">
                            <i className="fa-solid fa-car"></i>
                            <span>Car</span>
                        </Link>
                        <Link to="/buy?type=Van" className="category-card">
                            <i className="fa-solid fa-shuttle-van"></i>
                            <span>Van</span>
                        </Link>
                        <Link to="/buy?type=SUV" className="category-card">
                            <i className="fa-solid fa-car-side"></i>
                            <span>SUV</span>
                        </Link>
                        <Link to="/buy?type=Jeep" className="category-card">
                            <i className="fa-solid fa-truck-monster"></i>
                            <span>Jeep</span>
                        </Link>
                        <Link to="/buy?type=Motorbike" className="category-card">
                            <i className="fa-solid fa-motorcycle"></i>
                            <span>Motorbike</span>
                        </Link>
                        <Link to="/buy?type=Three-Wheeler" className="category-card">
                            <i className="fa-solid fa-motorcycle"></i>
                            <span>Three Wheeler</span>
                        </Link>
                        <Link to="/buy?type=Truck" className="category-card">
                            <i className="fa-solid fa-truck"></i>
                            <span>Truck</span>
                        </Link>
                        <Link to="/buy?type=Bus" className="category-card">
                            <i className="fa-solid fa-bus"></i>
                            <span>Bus</span>
                        </Link>
                        <Link to="/buy?type=Lorry" className="category-card">
                            <i className="fa-solid fa-truck-moving"></i>
                            <span>Lorry</span>
                        </Link>
                        <Link to="/buy?type=Bicycle" className="category-card">
                            <i className="fa-solid fa-bicycle"></i>
                            <span>Bicycle</span>
                        </Link>
                        <Link to="/parts" className="category-card">
                            <i className="fa-solid fa-gears"></i>
                            <span>Parts & Accessories</span>
                        </Link>
                        <Link to="/service-stations" className="category-card">
                            <i className="fa-solid fa-screwdriver-wrench"></i>
                            <span>Service Station</span>
                        </Link>
                    </div>
                </div>
            </section>
            {/* End of swapped sections */}

        </div>
    );
};

export default Home;

